"use client";

import { Box, Flex } from "@chakra-ui/react";
import { FiTrash2, FiX } from "react-icons/fi";
import type { ReactNode } from "react";
import { Text } from "@/components/ui";
import { useThemeTokens } from "@/lib/theme/tokens";

export function PublicFilterChip({
  label,
  removeLabel,
  onRemove,
}: {
  label: string;
  removeLabel: string;
  onRemove: () => void;
}) {
  const theme = useThemeTokens();

  return (
    <Box
      as="button"
      type="button"
      display="inline-flex"
      alignItems="center"
      gap={1}
      px={3}
      py={1}
      borderRadius="full"
      fontSize="sm"
      fontWeight="normal"
      lineHeight="1.4"
      bg={theme.accent.mutedBg}
      color={theme.text.primary}
      cursor="pointer"
      onClick={onRemove}
      aria-label={removeLabel}
      transition="opacity 0.2s ease"
      sx={{
        "@media (hover: hover)": {
          "&:hover": { opacity: 0.8 },
        },
      }}
    >
      <Text as="span" fontSize="sm">
        {label}
      </Text>
      <Box as="span" display="inline-flex" aria-hidden>
        <FiX size={14} />
      </Box>
    </Box>
  );
}

export function PublicFilterClearAll({ onClear }: { onClear: () => void }) {
  const theme = useThemeTokens();

  return (
    <Flex
      as="button"
      type="button"
      align="center"
      gap={1.5}
      px={3}
      py={1}
      borderRadius="full"
      fontSize="sm"
      fontWeight="normal"
      lineHeight="1.4"
      cursor="pointer"
      borderWidth="1px"
      borderColor={theme.borderGlass.default}
      bg={theme.accent.mutedBg}
      color={theme.text.secondary}
      onClick={onClear}
      aria-label="Clear all filters"
      transition="all 0.2s ease"
      sx={{
        "@media (hover: hover)": {
          "&:hover": {
            bg: theme.accent.primaryBg,
            borderColor: theme.accent.primaryBorder,
            color: theme.text.primary,
          },
        },
      }}
    >
      Clear all
      <FiTrash2 size={14} aria-hidden />
    </Flex>
  );
}

export function PublicFilterChipGroup({ children }: { children: ReactNode }) {
  return (
    <Flex gap={2} wrap="wrap" alignItems="center">
      {children}
    </Flex>
  );
}

export function PublicListingResultsMeta({
  chips,
  children,
}: {
  chips?: ReactNode;
  children?: ReactNode;
}) {
  if (!chips && !children) return null;

  return (
    <Flex
      direction="column"
      gap={chips && children ? 3 : 0}
      pt={2}
      pb={2}
    >
      {chips}
      {children}
    </Flex>
  );
}
